import type { AiProvider, AiChatSession } from "@/types/ai-chat";
import type { Run } from "@/stores/reviewStore";
import type { ChatTab } from "@/stores/chatTabStore";
import type { LocalTerminalTab } from "@/stores/localTerminalStore";
import type { AgentType } from "@/components/colony/AgentTypeAvatar";
import type { UnifiedAgentStatus } from "@/lib/utils/agent-status";
import {
  chatSessionToUnifiedStatus,
  chatTabToUnifiedStatus,
  terminalToUnifiedStatus,
  reviewRunToUnifiedStatus,
  needsAttention,
} from "@/lib/utils/agent-status";

// ── Types ──────────────────────────────────────────────────────────

export type AgentSource = "workflow" | "chat" | "terminal";

export interface UnifiedAgentRow {
  id: string;
  source: AgentSource;
  nativeId: string;
  projectId: number | null;
  projectName: string;
  title: string;
  status: UnifiedAgentStatus;
  startedAt: number | null;
  lastActivityAt: number | null;
  costUsd?: number;
  provider?: AiProvider;
  cwd?: string;
  /** localTerminalStore scope key — only set for terminal-source rows. */
  scopeKey?: string;
  agentType?: AgentType;
}

export interface UnifiedAgentGroup {
  projectId: number | null;
  projectName: string;
  rows: UnifiedAgentRow[];
}

// ── Mappers ────────────────────────────────────────────────────────

export function mapRunToUnifiedRow(
  run: Run,
  projectName: string,
): UnifiedAgentRow {
  return {
    id: `workflow:${run.id}`,
    source: "workflow",
    nativeId: run.id,
    projectId: run.projectId,
    projectName,
    title: run.title,
    status: reviewRunToUnifiedStatus(run.status),
    startedAt: run.startedAt ? new Date(run.startedAt).getTime() : null,
    lastActivityAt: run.completedAt
      ? new Date(run.completedAt).getTime()
      : run.startedAt
        ? new Date(run.startedAt).getTime()
        : null,
    costUsd: undefined,
    agentType: issueTypeToAgentType(run.issueType),
  };
}

export function mapChatToUnifiedRow(
  tab: ChatTab,
  session: AiChatSession | undefined,
  projectName: string,
): UnifiedAgentRow {
  const status = session
    ? chatSessionToUnifiedStatus(session.state)
    : chatTabToUnifiedStatus(tab.status);

  return {
    id: `chat:${tab.id}`,
    source: "chat",
    nativeId: tab.id,
    projectId: tab.projectId,
    projectName,
    title: session?.title ?? tab.name,
    status,
    startedAt: null,
    lastActivityAt: session?.events.length
      ? Date.now()
      : null,
    costUsd: session?.totalCostUsd,
    provider: tab.provider,
    cwd: session?.cwd,
  };
}

export function mapTerminalToUnifiedRow(
  tab: LocalTerminalTab,
  scopeKey: string,
  projectId: number | null,
  projectName: string,
  cwd?: string,
): UnifiedAgentRow {
  return {
    id: `terminal:${tab.id}`,
    source: "terminal",
    nativeId: tab.id,
    projectId,
    projectName,
    title: tab.name,
    status: terminalToUnifiedStatus(tab.activityStatus),
    startedAt: null,
    lastActivityAt: tab.lastOutputAt,
    scopeKey,
    cwd: cwd ?? (scopeKey.startsWith("cwd:") ? scopeKey.slice(4) : undefined),
  };
}

// ── Grouping ───────────────────────────────────────────────────────

export function groupByProject(rows: UnifiedAgentRow[]): UnifiedAgentGroup[] {
  const map = new Map<number | null, UnifiedAgentRow[]>();

  for (const row of rows) {
    const key = row.projectId;
    const arr = map.get(key);
    if (arr) arr.push(row);
    else map.set(key, [row]);
  }

  const groups: UnifiedAgentGroup[] = [];
  map.forEach((groupRows, projectId) => {
    groupRows.sort((a: UnifiedAgentRow, b: UnifiedAgentRow) => (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0));
    groups.push({
      projectId,
      projectName: groupRows[0].projectName,
      rows: groupRows,
    });
  });

  groups.sort((a, b) => {
    const aMax = a.rows[0]?.lastActivityAt ?? 0;
    const bMax = b.rows[0]?.lastActivityAt ?? 0;
    return bMax - aMax;
  });

  return groups;
}

// ── Needs attention ────────────────────────────────────────────────

const ATTENTION_ORDER: Record<string, number> = { error: 0, waiting_review: 1 };

export function collectNeedsAttention(
  groups: UnifiedAgentGroup[],
): UnifiedAgentRow[] {
  return groups
    .flatMap((g) => g.rows)
    .filter((r) => needsAttention(r.status))
    .sort((a, b) => {
      const bucket =
        (ATTENTION_ORDER[a.status] ?? 9) - (ATTENTION_ORDER[b.status] ?? 9);
      if (bucket !== 0) return bucket;
      return (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0);
    });
}

// ── Helpers ────────────────────────────────────────────────────────

const ISSUE_TO_AGENT: Record<string, AgentType> = {
  bug: "bug-fixer",
  feature: "feature-builder",
  refactor: "refactorer",
  hotfix: "bug-fixer",
};

function issueTypeToAgentType(issueType: string): AgentType | undefined {
  return ISSUE_TO_AGENT[issueType];
}
